import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export type SyncBankTransactionsResponse = {
  transactionsSynced: number;
  bankAccountIds: string[];
  syncedAt: string;
  stub?: boolean;
};

export function syncBankTransactionsErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/failed-precondition') {
      return 'No bank accounts to sync. Connect Qonto and wait for accounts to appear.';
    }
    if (code === 'functions/not-found') {
      return 'Bank account not found.';
    }
    if (code === 'functions/permission-denied') {
      return 'You do not have permission to sync this account.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to sync transactions.';
    }
    if (code === 'functions/unavailable') {
      return 'Qonto is temporarily unavailable. Try again in a moment.';
    }
    if (code === 'functions/internal' || code === 'functions/deadline-exceeded') {
      return 'Transaction sync failed. Try again.';
    }
  }
  return err instanceof Error ? err.message : 'Transaction sync failed.';
}

export async function syncBankTransactions(
  bankAccountId?: string
): Promise<SyncBankTransactionsResponse> {
  const fn = httpsCallable<{ bankAccountId?: string }, SyncBankTransactionsResponse>(
    functions,
    'syncBankTransactions'
  );
  const trimmedId = bankAccountId?.trim();
  const result = await fn(trimmedId ? { bankAccountId: trimmedId } : {});
  const transactionsSynced =
    typeof result.data.transactionsSynced === 'number' &&
    Number.isFinite(result.data.transactionsSynced)
      ? result.data.transactionsSynced
      : 0;
  const bankAccountIds = Array.isArray(result.data.bankAccountIds)
    ? result.data.bankAccountIds.filter((id): id is string => typeof id === 'string')
    : [];
  const syncedAt =
    typeof result.data.syncedAt === 'string' && result.data.syncedAt.trim()
      ? result.data.syncedAt
      : new Date().toISOString();
  return {
    transactionsSynced,
    bankAccountIds,
    syncedAt,
    stub: result.data.stub === true,
  };
}
